import { ComponentProps, useRef, useState, useEffect } from 'react'
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown'
import remarkBreaks from 'remark-breaks'
import remarkGfm from 'remark-gfm'
import { webchatClasses } from '../../../styles/classes'
import { TextBlock, type CommonBlockProps } from '../../../types'
import { Check, Files, ImageOff } from 'lucide-react'

type Props = TextBlock & CommonBlockProps
export const Text = ({ text }: Props) => {
  const {
    message: {
      blocks: { text: markdownStyles },
    },
  } = webchatClasses
  const [imageErrors, setImageErrors] = useState<Map<string, boolean>>(new Map())

  function checkIfImageHasError(imageUrl: string) {
    if (imageErrors.has(imageUrl)) {
      return
    }

    const img = new Image()
    img.src = imageUrl
    img.onerror = () => {
      setImageErrors((prev) => new Map(prev).set(imageUrl, true))
    }
    img.onload = () => {
      setImageErrors((prev) => new Map(prev).set(imageUrl, false))
    }
  }

  // Extract image URLs from markdown and check them in useEffect
  useEffect(() => {
    const imgRegex = /!\[.*?\]\((.*?)\)/g
    const urls: string[] = []
    let match

    while ((match = imgRegex.exec(text)) !== null) {
      const url = match[1]
      if (url && !urls.includes(url)) {
        urls.push(url)
      }
    }

    urls.forEach((imageUrl) => {
      checkIfImageHasError(imageUrl)
    })
  }, [text])

  const markdownComponents: ComponentProps<typeof ReactMarkdown>['components'] = {
    h1: ({ node, ...props }) => <h1 {...props} {...markdownStyles.heading1} />,
    h2: ({ node, ...props }) => <h2 {...props} {...markdownStyles.heading2} />,
    h3: ({ node, ...props }) => <h3 {...props} {...markdownStyles.heading3} />,
    h4: ({ node, ...props }) => <h4 {...props} {...markdownStyles.heading4} />,
    h5: 'h4',
    h6: 'h4',
    em: ({ node, ...props }) => <em {...props} {...markdownStyles.italic} />,
    strong: ({ node, ...props }) => <strong {...props} {...markdownStyles.bold} />,
    p: ({ node, ...props }) => <p {...props} {...markdownStyles.text} />,
    hr: ({ node, ...props }) => <hr {...props} {...markdownStyles.horizontalRule} />,
    a: ({ node, href, children, ...props }) => (
      <a
        {...props}
        {...markdownStyles.link}
        // Workaround to trigger link opening because of weird behavior causing the href to not work
        onMouseUp={(e) => {
          window.open(href, '_blank', 'noopener,noreferrer')
        }}
      >
        {children}
      </a>
    ),
    img: ({ node, ...props }) => {
      const imageUrl = props.src as string
      const hasError = imageErrors.get(imageUrl) || false

      return hasError ? (
        <div data-error={true} {...markdownStyles.image}>
          <ImageOff strokeWidth="1" />
        </div>
      ) : (
        <img {...props} {...markdownStyles.image} />
      )
    },
    ol: ({ node, ...props }) => <ol {...props} {...markdownStyles.orderedList} />,
    ul: ({ node, ...props }) => <ul {...props} {...markdownStyles.unorderedList} />,
    li: ({ node, ...props }) => <li {...props} {...markdownStyles.listItem} />,
    br: ({ node, ...props }) => <br {...props} {...markdownStyles.lineBreak} />,
    pre: ({ node, ...props }) => {
      const codeElement = node?.children[0]
      // @ts-expect-error - The node type from ReactMarkdown is not well defined
      const language = extractLanguage(codeElement?.properties?.className?.[0] || '')

      const [codeCopied, setCodeCopied] = useState(false)
      const codeRef = useRef<HTMLPreElement>(null)

      const handleCodeCopy = () => {
        if (!codeRef.current) return

        const codeContent = codeRef.current.textContent || ''

        navigator.clipboard
          .writeText(codeContent)
          .then(() => {
            setCodeCopied(true)

            setTimeout(() => {
              setCodeCopied(false)
            }, 1500)
          })
          .catch((err) => {
            console.error('Failed to copy code to clipboard:', err)
          })
      }

      return (
        <div {...markdownStyles.codeBlockContainer}>
          <div {...markdownStyles.codeBlockTitle}>
            {language ? <span>{language}</span> : 'Code'}
            {codeCopied ? (
              <Check {...markdownStyles.codeBlockCopyIcon} />
            ) : (
              <Files {...markdownStyles.codeBlockCopyIcon} onClick={handleCodeCopy}></Files>
            )}
          </div>
          <pre ref={codeRef} {...props} {...markdownStyles.codeBlock} />
        </div>
      )
    },
    input: ({ node, ...props }) => <input {...props} {...markdownStyles.input} disabled={false} />,
    code: ({ node, ...props }) => <code {...props} {...markdownStyles.code} />,
    table: ({ node, ...props }) => {
      return (
        <div {...markdownStyles.tableContainer}>
          <table {...props} {...markdownStyles.table} />
        </div>
      )
    },
    th: ({ node, ...props }) => <th {...props} {...markdownStyles.tableHeader} />,
    tr: ({ node, ...props }) => <tr {...props} {...markdownStyles.tableRow} />,
    td: ({ node, ...props }) => <td {...props} {...markdownStyles.tableData} />,
  }

  return (
    <ReactMarkdown
      urlTransform={(url) => (url.startsWith('tel:') ? url : defaultUrlTransform(url))}
      components={markdownComponents}
      remarkPlugins={[remarkGfm, remarkBreaks]}
    >
      {text}
    </ReactMarkdown>
  )
}

const extractLanguage = (className: string | undefined) => {
  if (!className) return ''
  const match = className.match(/language-(\w+)/)
  return match ? match[1] : ''
}
